import { useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatDate, formatDollar, unformatDollar } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import {
  buildRecordUpdateRequestBody,
  computeRecordUpdate,
  BILLING_TYPE_LABELS,
  type RecordUpdateType,
  type BillingType,
} from "@shared/recordUpdate";
import { hasAnyBreakdown, sumMoney } from "@shared/premium";
import type { Policy } from "@shared/schema";
import { useRecordPolicyUpdate } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { formatPolicyStatusLabel } from "./policy-status";

export const RECORD_UPDATE_TYPES: readonly RecordUpdateType[] = [
  "positive_endorsement",
  "negative_endorsement",
  "cancellation",
  "reinstatement",
];

export const RECORD_UPDATE_TYPE_LABELS: Record<RecordUpdateType, string> = {
  positive_endorsement: "Positive Endorsement",
  negative_endorsement: "Negative Endorsement",
  cancellation: "Cancellation",
  reinstatement: "Reinstatement",
};

const BILLING_TYPE_NONE = "__none__";

// On save the modal POSTs the client-computed record-update body (CSR-1971) to
// the AMS thin proxy `POST /api/policies/:id/record-updates` (CSR-1967), which
// forwards it to DC's validating writer. `policyId` is the POST target; `policy`
// provides the current premium breakdown for the live overview + the commission
// pre-fill.
export interface RecordUpdateModalProps {
  type: RecordUpdateType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  policyId: string;
  insuredId: string;
  policy: Policy;
}

interface FieldErrors {
  premium?: boolean;
  effectiveDate?: boolean;
}

function isoToday(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// Keep commission inputs within the shared 0–100 contract (commissionPctStringField)
// at the keystroke boundary: a single decimal, ≤2 places, clamped to 100.
function sanitizePercent(value: string): string {
  const [whole, ...rest] = value.replace(/[^\d.]/g, "").split(".");
  const normalized = rest.length ? `${whole}.${rest.join("").slice(0, 2)}` : whole;
  if (normalized === "" || normalized === ".") return normalized;
  return Number(normalized) > 100 ? "100" : normalized;
}

function isPositiveAmount(value: string): boolean {
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0;
}

// Accounting-style money for the overview: blank stays "--" (stable layout, no
// spurious $0.00 when a component is empty); negatives render in parentheses.
function formatSummaryAmount(value: string | null | undefined): string {
  if (!value) return "--";
  const isNegative = value.startsWith("-");
  const formatted = formatDollar(isNegative ? value.slice(1) : value);
  if (!formatted) return "--";
  return isNegative ? `(${formatted})` : formatted;
}

// Sublabel money always shows two decimals and $0.00 for blank (the prototype's
// "Current $0.00 + $175.00" breakdown), unlike the row value's "--". A negative
// current value (e.g. a policy premium after a cancellation) keeps its sign —
// formatDollar strips non-digits, so apply the sign here (CSR-1983).
function formatMoneyWithCents(value: string | null | undefined): string {
  const amount = Number(value || 0);
  const formatted = formatDollar(Math.abs(amount).toFixed(2));
  return amount < 0 ? `-${formatted}` : formatted;
}

function SummaryRow({
  label,
  sublabel,
  amount,
  emphasized,
  muted,
  testId,
}: {
  label: string;
  sublabel?: string;
  amount: string;
  emphasized?: boolean;
  muted?: boolean;
  testId: string;
}) {
  // formatSummaryAmount parenthesizes negatives; render those in red to match
  // the policy header (CSR-1986). Red wins over muted/foreground.
  const isNegative = amount.startsWith("(");
  const toneClass = isNegative
    ? "text-red-600"
    : muted
      ? "text-muted-foreground"
      : "text-foreground";
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className={cn("text-sm", emphasized && "font-bold", toneClass)}>
        {label}
        {sublabel && (
          <span className="block text-[11px] text-muted-foreground" data-testid={`${testId}-sublabel`}>
            {sublabel}
          </span>
        )}
      </span>
      <span
        className={cn("tabular-nums text-sm", emphasized && "font-bold", toneClass)}
        data-testid={testId}
      >
        {amount}
      </span>
    </div>
  );
}

export function RecordUpdateModal({
  type,
  open,
  onOpenChange,
  policyId,
  policy,
}: RecordUpdateModalProps) {
  const label = RECORD_UPDATE_TYPE_LABELS[type];
  const firstFieldRef = useRef<HTMLButtonElement>(null);
  const { toast } = useToast();
  const recordUpdate = useRecordPolicyUpdate();

  const [billingType, setBillingType] = useState<BillingType | null>(null);
  const [endorsementNumber, setEndorsementNumber] = useState("");
  const [premium, setPremium] = useState("");
  const [effectiveDate, setEffectiveDate] = useState(isoToday);
  const [brokerFee, setBrokerFee] = useState("");
  const [mgaFee, setMgaFee] = useState("");
  const [taxes, setTaxes] = useState("");
  const [agencyCommissionPct, setAgencyCommissionPct] = useState(
    policy.commissionPct ?? "",
  );
  const [agentCommissionPct, setAgentCommissionPct] = useState("0");
  const [errors, setErrors] = useState<FieldErrors>({});

  // A synced policy carries its total in `premium` with the breakdown components
  // null; only edited/DC-echoed policies populate `policyPremium`. Fall back to
  // `premium` ONLY when no breakdown component exists — otherwise `premium`
  // already includes the fees and adding them again double-counts (same guard as
  // EditPolicyDialog).
  const currentBreakdown = {
    policyPremium: policy.policyPremium ?? (hasAnyBreakdown(policy) ? null : policy.premium),
    brokerFee: policy.brokerFee,
    policyFees: policy.policyFees,
    taxes: policy.taxes,
  };
  const currentNonPremium = sumMoney([
    currentBreakdown.brokerFee,
    currentBreakdown.policyFees,
    currentBreakdown.taxes,
  ]);

  const summary = useMemo(
    () =>
      computeRecordUpdate({
        type,
        currentPremium: currentBreakdown,
        premium,
        brokerFee,
        mgaFee,
        taxes,
        agencyCommissionPct,
        agentCommissionPct,
        effectiveDate,
      }),
    [
      type,
      currentBreakdown.policyPremium,
      currentBreakdown.brokerFee,
      currentBreakdown.policyFees,
      currentBreakdown.taxes,
      premium,
      brokerFee,
      mgaFee,
      taxes,
      agencyCommissionPct,
      agentCommissionPct,
      effectiveDate,
    ],
  );

  const isEndorsement = type === "positive_endorsement" || type === "negative_endorsement";
  const premiumIsAdded = type === "positive_endorsement" || type === "reinstatement";
  // CSR-1987 — a cancellation SUBTRACTS the entered fees from the existing ones
  // (like the premium); the other three types add. Flip the sublabel sign to
  // match the computed result so the preview formula never contradicts it.
  const nonPremiumIsAdded = type !== "cancellation";
  const outstandingSublabel = `Current ${formatMoneyWithCents(currentBreakdown.policyPremium)} ${premiumIsAdded ? "+" : "-"} ${formatMoneyWithCents(premium)}`;
  const nonPremiumSublabel = `Current ${formatMoneyWithCents(currentNonPremium)} ${nonPremiumIsAdded ? "+" : "-"} ${formatMoneyWithCents(sumMoney([brokerFee, mgaFee, taxes]))}`;
  const statusDateSuffix = summary.cancellationDate
    ? ` (cancellation date ${formatDate(summary.cancellationDate)})`
    : "";

  const handleSubmit = () => {
    const nextErrors: FieldErrors = {
      premium: !isPositiveAmount(premium),
      effectiveDate: effectiveDate.trim() === "",
    };
    setErrors(nextErrors);
    if (nextErrors.premium || nextErrors.effectiveDate) return;

    const body = buildRecordUpdateRequestBody(
      {
        type,
        billingType,
        endorsementNumber: isEndorsement ? endorsementNumber : null,
        premium,
        brokerFee,
        mgaFee,
        taxes,
        agencyCommissionPct,
        agentCommissionPct,
        effectiveDate,
      },
      summary,
    );

    recordUpdate.mutate(
      { id: policyId, body },
      {
        onSuccess: () => {
          toast({ title: `${label} recorded` });
          onOpenChange(false);
        },
        onError: (error) => {
          toast({
            title: "Could not record the update",
            description: error instanceof Error ? error.message : undefined,
            variant: "destructive",
          });
        },
      },
    );
  };

  const setDollar =
    (setter: (value: string) => void) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setter(unformatDollar(e.target.value));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-xl max-h-[85vh] overflow-y-auto"
        data-testid="record-update-modal"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          firstFieldRef.current?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-xl" data-testid="record-update-modal-title">
            Record Update — {label}
          </DialogTitle>
          <DialogDescription>
            Record this policy update with its financial details.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="record-update-billing-type">Billing Type</Label>
            <Select
              value={billingType ?? BILLING_TYPE_NONE}
              onValueChange={(value) =>
                setBillingType(value === BILLING_TYPE_NONE ? null : (value as BillingType))
              }
            >
              <SelectTrigger
                id="record-update-billing-type"
                data-testid="record-update-billing-type"
                className="cursor-pointer"
                ref={firstFieldRef}
              >
                <SelectValue placeholder="Select billing type…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={BILLING_TYPE_NONE}>—</SelectItem>
                {(Object.keys(BILLING_TYPE_LABELS) as BillingType[]).map((value) => (
                  <SelectItem key={value} value={value}>
                    {BILLING_TYPE_LABELS[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isEndorsement && (
            <div>
              <Label htmlFor="record-update-endorsement-number">Endorsement Number</Label>
              <Input
                id="record-update-endorsement-number"
                data-testid="record-update-endorsement-number"
                placeholder="Carrier END #"
                value={endorsementNumber}
                onChange={(e) => setEndorsementNumber(e.target.value)}
              />
            </div>
          )}

          <div>
            <Label htmlFor="record-update-premium">Premium *</Label>
            <Input
              id="record-update-premium"
              data-testid="record-update-premium"
              inputMode="decimal"
              placeholder="$0.00"
              value={premium ? formatDollar(premium) : ""}
              onChange={setDollar(setPremium)}
              aria-invalid={errors.premium || undefined}
              className={cn(errors.premium && "border-destructive focus-visible:ring-destructive")}
            />
            {errors.premium && (
              <p className="mt-1 text-xs text-destructive" data-testid="record-update-premium-error">
                Enter a premium greater than $0.
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="record-update-effective-date">Effective Date *</Label>
            <Input
              id="record-update-effective-date"
              data-testid="record-update-effective-date"
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              aria-invalid={errors.effectiveDate || undefined}
              className={cn(
                "cursor-pointer",
                errors.effectiveDate && "border-destructive focus-visible:ring-destructive",
              )}
            />
            {errors.effectiveDate && (
              <p className="mt-1 text-xs text-destructive" data-testid="record-update-effective-date-error">
                Effective date is required.
              </p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="record-update-broker-fee">Broker Fees</Label>
              <Input
                id="record-update-broker-fee"
                data-testid="record-update-broker-fee"
                inputMode="decimal"
                placeholder="$0.00"
                value={brokerFee ? formatDollar(brokerFee) : ""}
                onChange={setDollar(setBrokerFee)}
              />
            </div>
            <div>
              <Label htmlFor="record-update-mga-fee">MGA Fee</Label>
              <Input
                id="record-update-mga-fee"
                data-testid="record-update-mga-fee"
                inputMode="decimal"
                placeholder="$0.00"
                value={mgaFee ? formatDollar(mgaFee) : ""}
                onChange={setDollar(setMgaFee)}
              />
            </div>
            <div>
              <Label htmlFor="record-update-taxes">Taxes</Label>
              <Input
                id="record-update-taxes"
                data-testid="record-update-taxes"
                inputMode="decimal"
                placeholder="$0.00"
                value={taxes ? formatDollar(taxes) : ""}
                onChange={setDollar(setTaxes)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="record-update-agency-commission">Agency Commission (%)</Label>
              <Input
                id="record-update-agency-commission"
                data-testid="record-update-agency-commission"
                inputMode="decimal"
                placeholder="0"
                value={agencyCommissionPct}
                onChange={(e) => setAgencyCommissionPct(sanitizePercent(e.target.value))}
              />
            </div>
            <div>
              <Label htmlFor="record-update-agent-commission">Agent Commission (%)</Label>
              <Input
                id="record-update-agent-commission"
                data-testid="record-update-agent-commission"
                inputMode="decimal"
                placeholder="0"
                value={agentCommissionPct}
                onChange={(e) => setAgentCommissionPct(sanitizePercent(e.target.value))}
              />
            </div>
          </div>

          <div
            role="status"
            aria-live="polite"
            className="space-y-2 rounded-lg border border-border bg-muted/40 p-3"
            data-testid="record-update-summary"
          >
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              {label} Overview
            </p>
            <div className="space-y-1.5">
              <SummaryRow
                label="Total Outstanding Premium"
                sublabel={outstandingSublabel}
                amount={formatSummaryAmount(summary.totalOutstandingPremium)}
                testId="record-update-summary-outstanding"
              />
              <SummaryRow
                label="Total Non-Premium"
                sublabel={nonPremiumSublabel}
                amount={formatSummaryAmount(summary.totalNonPremium || "0.00")}
                testId="record-update-summary-non-premium"
              />
            </div>
            <div className="border-t border-border pt-2">
              <SummaryRow
                label="Total"
                amount={formatSummaryAmount(summary.total)}
                emphasized
                testId="record-update-summary-total"
              />
            </div>
            <div className="space-y-1.5 border-t border-border pt-2">
              <SummaryRow
                label={`Agency Commission (${Number(agencyCommissionPct) || 0}%)`}
                amount={formatSummaryAmount(summary.agencyCommissionAmount)}
                muted
                testId="record-update-summary-agency-commission"
              />
              <SummaryRow
                label={`Agent Commission (${Number(agentCommissionPct) || 0}%)`}
                amount={formatSummaryAmount(summary.agentCommissionAmount)}
                muted
                testId="record-update-summary-agent-commission"
              />
              <p className="text-[11px] text-muted-foreground">
                Commissions do not affect the total.
              </p>
            </div>
            {summary.status && (
              <p
                className="border-t border-border pt-2 text-xs text-muted-foreground"
                data-testid="record-update-summary-status"
              >
                On save, the policy status will change to{" "}
                <span className="font-semibold text-foreground">
                  {formatPolicyStatusLabel(summary.status)}
                </span>
                {statusDateSuffix}.
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            className="rounded-full cursor-pointer"
            onClick={() => onOpenChange(false)}
            data-testid="record-update-cancel"
          >
            Cancel
          </Button>
          <Button
            className="rounded-full cursor-pointer"
            onClick={handleSubmit}
            disabled={recordUpdate.isPending}
            data-testid="record-update-save"
          >
            {recordUpdate.isPending ? "Saving…" : "Save Update"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
